import {
  RedeDiagramaConexao,
  RedeDiagramaEntidadeTipo,
  RedeDiagramaMonitorStatus,
  RedeDiagramaNode,
  RedeDiagramaPortId,
} from '../../../services/redeDiagramaService';
import { STATUS_COLORS } from './monitoramentoRedeUtils';

export const SHAPE_NAME = 'dispositivo-rede-node';
export const ENERGIA_SHAPE_NAME = 'energia-rede-node';
export const DIAGRAM_NODE_WIDTH = 220;
export const DIAGRAM_NODE_HEIGHT = 84;
export const DIAGRAM_ENERGIA_NODE_WIDTH = 96;
export const DIAGRAM_ENERGIA_NODE_HEIGHT = 96;

const HORIZONTAL_GAP = 48;
const VERTICAL_GAP = 72;
const PADDING = 80;
const COLS = 4;

export interface DiagramNodeData {
  node: RedeDiagramaNode;
}

export interface DiagramNodeSpec {
  id: string;
  shape: string;
  x: number;
  y: number;
  width: number;
  height: number;
  data: DiagramNodeData;
}

export interface DiagramEdgeSpec {
  id: string;
  source: string;
  target: string;
  sourcePort: RedeDiagramaPortId;
  targetPort: RedeDiagramaPortId;
  conexaoId: number;
  stroke: string;
  strokeWidth: number;
  status: RedeDiagramaMonitorStatus | 'neutral';
  needsPortPersist?: boolean;
}

export interface DiagramGraphData {
  nodes: DiagramNodeSpec[];
  edges: DiagramEdgeSpec[];
}

export function makeNodeId(tipo: RedeDiagramaEntidadeTipo, id: number): string {
  return `${tipo}-${id}`;
}

export function parseNodeId(nodeId: string): { tipo: RedeDiagramaEntidadeTipo; id: number } | null {
  const match = nodeId.match(/^(dispositivo_rede|ftth_olt|server_nas)-(\d+)$/);
  if (!match) return null;
  const id = Number(match[2]);
  if (!Number.isInteger(id) || id <= 0) return null;
  return {
    tipo: match[1] as RedeDiagramaEntidadeTipo,
    id,
  };
}

function defaultPosition(index: number): { x: number; y: number } {
  const col = index % COLS;
  const row = Math.floor(index / COLS);
  return {
    x: PADDING + col * (DIAGRAM_NODE_WIDTH + HORIZONTAL_GAP),
    y: PADDING + row * (DIAGRAM_NODE_HEIGHT + VERTICAL_GAP),
  };
}

function resolveNodePosition(node: RedeDiagramaNode, index: number): { x: number; y: number } {
  if (
    node.pos_x != null &&
    node.pos_y != null &&
    Number.isFinite(node.pos_x) &&
    Number.isFinite(node.pos_y)
  ) {
    return { x: node.pos_x, y: node.pos_y };
  }
  return defaultPosition(index);
}

function resolveEdgeStatus(
  nodesById: Map<string, RedeDiagramaNode>,
  targetId: string
): RedeDiagramaMonitorStatus | 'neutral' {
  const target = nodesById.get(targetId);
  if (!target) return 'neutral';
  return target.monitor_status;
}

export function isDiagramPortId(value: unknown): value is RedeDiagramaPortId {
  return value === 'top' || value === 'right' || value === 'bottom' || value === 'left';
}

export function pickDefaultPorts(
  source: Pick<DiagramNodeSpec, 'x' | 'y' | 'width' | 'height'>,
  target: Pick<DiagramNodeSpec, 'x' | 'y' | 'width' | 'height'>
): { sourcePort: RedeDiagramaPortId; targetPort: RedeDiagramaPortId } {
  const scx = source.x + source.width / 2;
  const scy = source.y + source.height / 2;
  const tcx = target.x + target.width / 2;
  const tcy = target.y + target.height / 2;
  const dx = tcx - scx;
  const dy = tcy - scy;

  if (Math.abs(dx) >= Math.abs(dy)) {
    if (dx >= 0) return { sourcePort: 'right', targetPort: 'left' };
    return { sourcePort: 'left', targetPort: 'right' };
  }

  if (dy >= 0) return { sourcePort: 'bottom', targetPort: 'top' };
  return { sourcePort: 'top', targetPort: 'bottom' };
}

export function buildEdgeEndpoint(cellId: string, port: RedeDiagramaPortId) {
  return { cell: cellId, port };
}

export function isEnergiaDiagramNode(node: RedeDiagramaNode): boolean {
  return node.tipo_label === 'energia' || node.dispositivo?.tipo === 'energia';
}

function buildDiagramNodeSpec(node: RedeDiagramaNode, index: number): DiagramNodeSpec {
  const position = resolveNodePosition(node, index);
  const energia = isEnergiaDiagramNode(node);

  return {
    id: node.node_id,
    shape: energia ? ENERGIA_SHAPE_NAME : SHAPE_NAME,
    x: position.x,
    y: position.y,
    width: energia ? DIAGRAM_ENERGIA_NODE_WIDTH : DIAGRAM_NODE_WIDTH,
    height: energia ? DIAGRAM_ENERGIA_NODE_HEIGHT : DIAGRAM_NODE_HEIGHT,
    data: { node },
  };
}

export function buildDiagramGraph(
  nodes: RedeDiagramaNode[],
  conexoes: RedeDiagramaConexao[]
): DiagramGraphData {
  const nodesById = new Map(nodes.map((n) => [n.node_id, n]));

  const nodeSpecs: DiagramNodeSpec[] = nodes.map((node, index) => buildDiagramNodeSpec(node, index));

  const edges: DiagramEdgeSpec[] = [];

  for (const conexao of conexoes) {
    const source = makeNodeId(conexao.origem_tipo, conexao.origem_id);
    const target = makeNodeId(conexao.destino_tipo, conexao.destino_id);

    if (!nodesById.has(source) || !nodesById.has(target)) {
      continue;
    }

    const status = resolveEdgeStatus(nodesById, target);
    const stroke =
      status === 'neutral'
        ? conexao.style_color
        : STATUS_COLORS[status];

    const sourceNodeSpec = nodeSpecs.find((node) => node.id === source);
    const targetNodeSpec = nodeSpecs.find((node) => node.id === target);
    const hasSavedPorts = isDiagramPortId(conexao.origem_port) && isDiagramPortId(conexao.destino_port);

    let sourcePort = isDiagramPortId(conexao.origem_port) ? conexao.origem_port : null;
    let targetPort = isDiagramPortId(conexao.destino_port) ? conexao.destino_port : null;

    if ((!sourcePort || !targetPort) && sourceNodeSpec && targetNodeSpec) {
      const picked = pickDefaultPorts(sourceNodeSpec, targetNodeSpec);
      sourcePort = sourcePort ?? picked.sourcePort;
      targetPort = targetPort ?? picked.targetPort;
    }

    if (!sourcePort || !targetPort) {
      sourcePort = 'right';
      targetPort = 'left';
    }

    edges.push({
      id: `conexao-${conexao.id}`,
      source,
      target,
      sourcePort,
      targetPort,
      conexaoId: conexao.id,
      stroke,
      strokeWidth: conexao.style_width,
      status,
      needsPortPersist: !hasSavedPorts,
    });
  }

  return { nodes: nodeSpecs, edges };
}

export function getTipoDiagramaLabel(tipoLabel: string, nativo: boolean): string {
  if (nativo) {
    if (tipoLabel === 'olt') return 'OLT';
    if (tipoLabel === 'nas') return 'NAS';
  }
  const labels: Record<string, string> = {
    switch: 'Switch',
    servidor: 'Servidor',
    roteador: 'Roteador',
    radio_ap: 'Rádio AP',
    radio_station: 'Rádio Station',
    cliente: 'Cliente',
    servicos: 'Serviços externos',
    energia: 'Energia',
  };
  return labels[tipoLabel] ?? tipoLabel;
}

export function getNodeIpExibicao(node: RedeDiagramaNode): string {
  if (node.ip) return node.ip;
  if (node.dispositivo?.tipo === 'cliente') return 'RADIUS (dinâmico)';
  return '—';
}
